import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/auth';
import Sidebar from './components/shell/Sidebar';
import Topbar from './components/shell/Topbar';
import ModuleGuard from './components/auth/ModuleGuard';
import Dashboard from './pages/Dashboard';
import Configuracion from './pages/Configuracion';
import ProduccionDiaria from './pages/ProduccionDiaria';
import AnalisisProduccion from './pages/AnalisisProduccion';
import AlmacenInventario from './pages/AlmacenInventario';
import Despacho from './pages/Despacho';
import CostosROI from './pages/CostosROI';
import CuentasPorCobrar from './pages/CuentasPorCobrar';
import GestionUsuarios from './pages/GestionUsuarios';
import SinPermiso from './pages/SinPermiso';
import LoginPage from './pages/LoginPage';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F8FAF8]">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Cargando AVIPORK...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) {
    return <SinPermiso moduleName="Gestión de Usuarios" />;
  }
  return children;
};

const AppLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar
        open={sidebarOpen}
        onOpenChange={setSidebarOpen}
        collapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
      />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          onMenuClick={() => setSidebarOpen(true)}
          collapsed={sidebarCollapsed}
        />
        <main className="flex-1 overflow-y-auto bg-[#F8FAF8]">
          <div className="px-4 sm:px-6 lg:px-8 py-5 max-w-[1400px] mx-auto">
            <Routes>
              <Route path="/" element={
                <ModuleGuard moduleKey="dashboard" moduleName="Dashboard"><Dashboard /></ModuleGuard>
              } />
              <Route path="/configuracion" element={
                <ModuleGuard moduleKey="lotes" moduleName="Lotes y Naves"><Configuracion /></ModuleGuard>
              } />
              <Route path="/produccion" element={
                <ModuleGuard moduleKey="produccion" moduleName="Producción Diaria"><ProduccionDiaria /></ModuleGuard>
              } />
              <Route path="/analisis" element={
                <ModuleGuard moduleKey="analisis" moduleName="Análisis de Producción"><AnalisisProduccion /></ModuleGuard>
              } />
              <Route path="/almacen" element={
                <ModuleGuard moduleKey="almacen" moduleName="Almacén e Inventario"><AlmacenInventario /></ModuleGuard>
              } />
              <Route path="/despacho" element={
                <ModuleGuard moduleKey="despacho" moduleName="Despacho"><Despacho /></ModuleGuard>
              } />
              <Route path="/costos" element={
                <ModuleGuard moduleKey="costos" moduleName="Costos y ROI"><CostosROI /></ModuleGuard>
              } />
              <Route path="/cxc" element={
                <ModuleGuard moduleKey="cxc" moduleName="Cuentas por Cobrar"><CuentasPorCobrar /></ModuleGuard>
              } />
              <Route path="/usuarios" element={
                <SuperAdminRoute><GestionUsuarios /></SuperAdminRoute>
              } />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPageWrapper />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

const LoginPageWrapper = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F8FAF8]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <LoginPage />;
};

export default App;
